<template>
  <div>
    <OrderLayout
      :is-loading="fetchingData"
      :error="null"
      :store="store"
      :hero-store="heroStore"
      :selected-location="selectedLocation"
      :cart-count="totalItems"
      :cart-items="cartItems"
      :is-sidebar-visible="isSidebarVisible"
      :is-cart-open="isCartOpen"
      :is-cart-pinned="isCartPinned"
      :has-cart-updates="hasCartUpdates"
      :filtered-menu="filteredMenu"
      :selected-item="selectedItem"
      :order-config="orderConfig"
      :is-store-closed="false"
      :on-cart-click="handleCartClick"
      :on-cart-close="() => cartManagement.setCartOpen(false)"
      :on-update-quantity="cartManagement.updateQuantity"
      :on-remove-item="cartManagement.removeItem"
      :on-checkout="goToCheckout"
      :on-view-check="() => goToCheckout({ viewCheck: true })"
      :on-pin="handlePin"
      :on-unpin="handleUnpin"
      :on-item-click="modalManagement.setItemModalOpen"
      :on-item-modal-close="handleItemModalClose"
      :on-add-to-cart="cartManagement.addToCart"
      :editing-cart-id="editingCartId"
      :on-edit-item="handleEditItem"
      :on-update-cart-item="handleUpdateCartItem"
      :show-location-selector="false"
      :use-table-cart="true"
      :show-order-type-options="false"
      :order-number="currentOrderId"
      :table-name="tableName"
    >
      <!-- Mobile checkout button for table orders -->
      <template #additional-content>
        <div
          class="fixed bottom-0 left-0 right-0 lg:hidden bg-white border-t border-gray-200 p-4 z-10"
        >
          <button
            v-if="items.length > 0"
            @click="goToCheckout"
            class="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-4 px-6 rounded-lg text-lg transition-colors"
          >
            Send Order ({{ items.length }}) - {{ formatPrice(grand_total) }}
          </button>
          <button
            v-else-if="existingOrder"
            @click="goToCheckout({ viewCheck: true })"
            class="w-full bg-primary-600 hover:bg-primary-700 text-white font-bold py-4 px-6 rounded-lg text-lg transition-colors"
          >
            View Check - {{ formatPrice(existingOrder?.grand_total ?? 0) }}
          </button>
        </div>
      </template>
    </OrderLayout>

    <!-- Invalid Table Modal -->
    <TableStoreClosedModal
      :is-open="showInvalidTableModal"
      error-type="invalid_table"
      :error-message="tableError"
    />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import OrderLayout from "@/layouts/OrderLayout.vue";
import TableStoreClosedModal from "@/components/TableOrder/TableStoreClosedModal.vue";
import toastHelper from "@/helpers/toastHelper";
import { useTableCartManagement } from "@/composables/useTableCartManagement";
import { useModalManagement } from "@/composables/useModalManagement";
import { useCategoryNavigation } from "@/composables/useCategoryNavigation";
import getStoreInfo, { decodeTableHash } from "@/helpers/apiHelper";
import { resolveStoreAlias } from "@/helpers/urlHelper";

export default {
  name: "TableOrder",
  components: {
    OrderLayout,
    TableStoreClosedModal
  },
  data() {
    return {
      fetchingData: true,
      tableError: null,
      hasCartUpdates: false,
      showInvalidTableModal: false,
      localTableName: null,
      editingCartId: null
    };
  },
  created() {
    this._cartManagement = useTableCartManagement(this.$store, this.$router);
    this._modalManagement = useModalManagement(this.$store);
    this._categoryNavigation = useCategoryNavigation(this.$store);
  },
  async beforeMount() {
    await this.initializeTableOrder();
    await this.initializeTableInfo();
    this.updateDocumentTitle();
  },
  mounted() {
    this._categoryNavigation.handleCategoryFromUrl();
    this._categoryNavigation.setupHashChangeListener(
      this._categoryNavigation.handleCategoryFromUrl
    );
  },
  beforeDestroy() {
    this._categoryNavigation.removeHashChangeListener(
      this._categoryNavigation.handleCategoryFromUrl
    );
  },
  beforeRouteLeave(to, from, next) {
    // TableOrder <-> TableCheckout is one continuous ordering session (every
    // round: browse -> checkout -> back to menu). Tearing down the Ably
    // connection on every hop between them closes a real-time window where a
    // staff-side edit (e.g. added via POS) can land in that gap and never
    // reach this tab until a manual refresh. Only clean up when actually
    // leaving the table-ordering flow.
    if (to.name !== "TableOrder" && to.name !== "TableCheckout") {
      this.$store.dispatch("tableOrder/cleanupAbly");
    }
    next();
  },
  watch: {
    $route() {
      this._categoryNavigation.handleCategoryFromUrl();
    },
    isCartOpen(newVal) {
      if (newVal) {
        this.hasCartUpdates = false;
      }
    },
    tableError(newVal, oldVal) {
      // Show modal when table error is set (including empty string for default message)
      if (newVal !== null && newVal !== undefined && newVal !== oldVal) {
        this.showInvalidTableModal = true;
      } else if (newVal === null || newVal === undefined) {
        this.showInvalidTableModal = false;
      }
    },
    "store.name"() {
      this.updateDocumentTitle();
    }
  },
  methods: {
    getStoreDisplayName(store) {
      return (
        store?.displayName ||
        store?.display_name ||
        store?.displayname ||
        store?.location_name ||
        store?.name ||
        ""
      );
    },
    getLocationDisplayName(location) {
      return (
        location?.displayName ||
        location?.display_name ||
        location?.displayname ||
        location?.location_name ||
        ""
      );
    },
    getDisplayStoreName() {
      const locationName = this.getLocationDisplayName(this.selectedLocation);
      if (locationName) {
        return locationName;
      }
      return this.getStoreDisplayName(this.store);
    },
    updateDocumentTitle() {
      const storeName = this.getDisplayStoreName();
      const location = this.selectedLocation || {};
      const storeData = this.$store.getters.getStore || {};
      const city = location.city || storeData.city || "";
      const state = location.state || storeData.state || "";
      const locationLabel = [city, state].filter(Boolean).join(", ");
      document.title =
        storeName && storeName.toString().trim()
          ? locationLabel
            ? `${storeName} - ${locationLabel} | Online Ordering | Biyo POS`
            : `${storeName} | Online Ordering | Biyo POS`
          : "Order Online | Biyo POS";
    },
    async initializeTableOrder() {
      const { storeAlias, tableHash } = this.$route.params;

      try {
        this.fetchingData = true;
        this.tableError = null;

        await this.$store.dispatch("tableOrder/initializeTableOrder", {
          storeAlias,
          tableHash
        });

        await this.$store.dispatch("tableOrder/initializeAbly", {
          onOrderUpdate: (notification) => {
            this.showNotification(
              notification.type === "order_closed"
                ? "Order Closed"
                : "Order Updated",
              notification.message,
              notification.variant
            );
            if (notification.type === "order_updated") {
              this.hasCartUpdates = true;
            }
          }
        });

        this.fetchingData = false;
      } catch (error) {
        console.error("[TableOrder] Error initializing table order:", error);
        this.fetchingData = false;

        // Handle specific error types
        if (error.isNotFound || error.status === 404) {
          await this.$router.push({ name: "NotFound" });
          return;
        }

        // Provide user-friendly error messages
        if (error.message && error.message.includes("base URL")) {
          this.tableError = "Store configuration error. Please try again.";
        } else if (error.message && error.message.includes("Table")) {
          this.tableError = "Table not found.";
        } else {
          // Default error message for invalid table
          this.tableError = "";
        }
      }
    },
    showNotification(title, message, variant = "info") {
      toastHelper.showToast(this.$store, title, message, variant);
    },
    async initializeTableInfo() {
      const tableHash = this.$route.params.tableHash;
      const getTableId = this.$store.getters["tableOrder/getTableId"];

      if (!getTableId && tableHash) {
        try {
          const storeInfoResponse = await getStoreInfo(
            resolveStoreAlias(this.$route)
          );

          if (storeInfoResponse.status === 404) {
            await this.$router.push({ name: "NotFound" });
            return;
          }

          if (!storeInfoResponse.ok) {
            throw new Error("Failed to fetch store information");
          }

          const storeInfo = await storeInfoResponse.json();

          if (storeInfo.dashboard_link) {
            await this.$store.dispatch(
              "store/setBaseUrl",
              storeInfo.dashboard_link,
              {
                root: true
              }
            );
          }

          const decodeResponse = await decodeTableHash(tableHash);
          if (decodeResponse.data) {
            this.localTableName = decodeResponse.data.table_name;
            await this.$store.dispatch("tableOrder/setTableInfo", {
              tableId: decodeResponse.data.table_id,
              tableName: decodeResponse.data.table_name,
              tableHash: tableHash
            });
          }
        } catch (error) {
          console.error("[TableOrder] Error initializing table info:", error);
        }
      }
    },
    handleCartClick() {
      this.hasCartUpdates = false;
      this._cartManagement.setCartOpen(true);
    },
    handlePin() {
      this._cartManagement.setCartPinned(true, this.cartItems);
    },
    handleUnpin() {
      this._cartManagement.setCartPinned(false, this.cartItems);
    },
    handleItemModalClose() {
      this._modalManagement.setItemModalOpen(null);
      this.editingCartId = null;
    },
    handleEditItem(cartItem) {
      this._cartManagement.setCartOpen(false);
      this._modalManagement.setItemModalOpen({
        ...cartItem,
        notes: cartItem.note,
        modifiers: cartItem.selectedModifiers
      });
      this.editingCartId = cartItem.cartId;
    },
    handleUpdateCartItem(cartId, payload) {
      this._cartManagement.updateCartItem(cartId, payload);
      this.editingCartId = null;
    },
    goToCheckout(options = {}) {
      const query = { ...options };
      // If no new items, default to view check mode
      const existingOrder = this.$store.getters["tableOrder/getExistingOrder"];
      if (this.items.length === 0 && existingOrder) {
        query.viewCheck = "true";
      }
      // If explicitly requested (from button click), ensure it's string
      if (query.viewCheck) {
        query.viewCheck = "true";
      }

      this._cartManagement.checkout({
        ...query,
        storeAlias: resolveStoreAlias(this.$route),
        tableHash: this.$route.params.tableHash
      });
    }
  },
  computed: {
    ...mapGetters([
      "isSidebarVisible",
      "filteredMenu",
      "selectedItem",
      "isCartOpen",
      "isCartPinned",
      "getStore",
      "getCurrencySymbol",
      "formatPrice"
    ]),
    totalItems() {
      return this.$store.getters["tableOrder/totalItems"] || 0;
    },
    cartItems() {
      return this.$store.getters["tableOrder/getCartItems"] || [];
    },
    orderConfig() {
      return (
        this.$store.getters["tableOrder/getOrderConfig"] || {
          type: "dine_in",
          timing: "asap"
        }
      );
    },
    items() {
      return this.$store.getters["tableOrder/getItems"] || [];
    },
    selectedLocation() {
      return this.$store.getters.selectedLocation;
    },
    existingOrder() {
      return this.$store.getters["tableOrder/getExistingOrder"];
    },
    store() {
      const stores = this.$store.getters.getStores || [];
      const storeData = this.$store.getters.getStore || {};
      return {
        name:
          this.getStoreDisplayName(storeData) ||
          this.$store.getters.getStoreName ||
          "",
        logo: storeData.logo || storeData.image || "",
        storesCount: stores.length,
        alias: resolveStoreAlias(this.$route)
      };
    },
    heroStore() {
      const selectedLocation = this.$store.getters.selectedLocation;
      const storeData = this.$store.getters.getStore || {};

      if (selectedLocation) {
        return {
          name: this.getDisplayStoreName(),
          address: selectedLocation.address || "",
          phone: selectedLocation.phone || "",
          image:
            selectedLocation.online_ordering_image ||
            selectedLocation.onlineOrderingImage ||
            storeData.online_ordering_image ||
            storeData.onlineOrderingImage ||
            selectedLocation.image ||
            storeData.image ||
            this.store.logo ||
            null,
          description:
            selectedLocation.description || storeData.description || "",
          rating: selectedLocation.rating || storeData.rating || null,
          workingHours: null
        };
      }

      return {
        name: this.store.name,
        address: storeData.address || "",
        phone: storeData.phone || "",
        image:
          storeData.online_ordering_image ||
          storeData.onlineOrderingImage ||
          storeData.image ||
          this.store.logo ||
          null,
        description: storeData.description || "",
        rating: storeData.rating || null,
        workingHours: null
      };
    },
    currency() {
      return this.getCurrencySymbol || "$";
    },
    grand_total() {
      const subtotal = this.$store.getters["tableOrder/subtotal"] || 0;
      const totalTax = this.$store.getters["tableOrder/getTotalTax"] || 0;
      return subtotal + totalTax;
    },
    currentOrderId() {
      return this.$store.getters["tableOrder/getCurrentOrderId"];
    },
    tableName() {
      return (
        this.localTableName || this.$store.getters["tableOrder/getTableName"]
      );
    },
    cartManagement() {
      return this._cartManagement;
    },
    modalManagement() {
      return this._modalManagement;
    }
  }
};
</script>
